import { useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { GET_GROUP_CHANNELS } from '../../../gql/queries/channelQueries'
import { CREATE_CHANNEL, DELETE_CHANNEL } from '../../../gql/mutations/channelMutations'
import { Channel } from '../../../types/assignmentGroups'

export const useGroupChannels = (groupId?: string | number) => {
  const numericGroupId = groupId ? parseInt(String(groupId), 10) : null

  const { data, loading, error, refetch } = useQuery(GET_GROUP_CHANNELS, {
    variables: { scope: 'group', targetId: numericGroupId },
    skip: !numericGroupId,
  })

  const [createChannelMutation, { loading: saving }] = useMutation(CREATE_CHANNEL)
  const [deleteChannelMutation] = useMutation(DELETE_CHANNEL)

  const saveChannel = useCallback(
    async (channelData: Partial<Channel>) => {
      if (!numericGroupId) return null

      try {
        const result = await createChannelMutation({
          variables: {
            id: channelData.id ? parseInt(String(channelData.id), 10) : undefined,
            scope: 'group',
            targetId: numericGroupId,
            provider: channelData.provider,
            channelType: channelData.channel_type,
            phoneNumber: channelData.phone_number,
            credentials: JSON.stringify(channelData.credentials || {}),
            assignedMemberIds: channelData.assigned_member_ids,
            isActive: channelData.is_active,
          },
        })

        if (result.data?.createChannel?.success) {
          refetch()
          return result.data.createChannel.channel
        } else {
          const errorMsg = result.data?.createChannel?.error || 'Failed to save channel'
          console.error('GraphQL error:', errorMsg)
          throw new Error(errorMsg)
        }
      } catch (err: any) {
        const errorMsg = err?.message || err?.graphQLErrors?.[0]?.message || 'Error saving channel'
        console.error('Error saving channel:', errorMsg, err)
        throw new Error(errorMsg)
      }
    },
    [numericGroupId, createChannelMutation, refetch]
  )

  const deleteChannel = useCallback(
    async (channelId: number) => {
      try {
        const result = await deleteChannelMutation({
          variables: { id: channelId },
        })

        if (result.data?.deleteChannel?.success) {
          refetch()
          return true
        } else {
          throw new Error(result.data?.deleteChannel?.error || 'Failed to delete channel')
        }
      } catch (err) {
        console.error('Error deleting channel:', err)
        return false
      }
    },
    [deleteChannelMutation, refetch]
  )

  const channels: Channel[] = (data?.channels || []).map((ch: any) => ({
    id: ch.id,
    provider: ch.provider,
    channel_type: ch.channelType,
    phone_number: ch.phoneNumber,
    webhook_token: ch.webhookToken,
    assigned_member_ids: ch.assignedMemberIds,
    is_active: ch.isActive,
    credentials: ch.credentials,
    webhook_url_twilio: ch.webhookUrlTwilio,
    webhook_url_botmaker: ch.webhookUrlBotmaker,
  }))

  return { channels, loading, saving, refetch, saveChannel, deleteChannel }
}
